import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  EntityContextType,
  isTeamMember,
  usePeopleService,
  useProjectsService,
} from 'nekst-api';
import { useChatWidgetContext } from './ChatWidgetContext';

interface DetectedEntity {
  type: EntityContextType;
  id: number;
}

function detectEntityFromUrl(pathname: string): DetectedEntity | null {
  const transactionMatch = pathname.match(/^\/transactions\/(\d+)/);
  if (transactionMatch) {
    return { type: 'transaction', id: parseInt(transactionMatch[1], 10) };
  }

  const personMatch = pathname.match(/^\/people\/(\d+)/);
  if (personMatch) {
    return { type: 'contact', id: parseInt(personMatch[1], 10) };
  }

  const memberMatch = pathname.match(/^\/settings\/members\/(\d+)/);
  if (memberMatch) {
    return { type: 'team_member', id: parseInt(memberMatch[1], 10) };
  }

  return null;
}

export function useAutoContext() {
  const location = useLocation();
  const { addContext, clearContext } = useChatWidgetContext();
  const projectsService = useProjectsService();
  const peopleService = usePeopleService();

  useEffect(() => {
    clearContext();

    const detected = detectEntityFromUrl(location.pathname);
    if (!detected) {
      return;
    }

    const fetchAndAddContext = async () => {
      try {
        if (detected.type === 'transaction') {
          const project = await projectsService.getById(detected.id);
          addContext({ type: 'transaction', id: detected.id, name: project.name });
        } else {
          const person = await peopleService.getById(detected.id);
          const name = `${person.profile.name.firstName} ${person.profile.name.lastName}`;
          const type: EntityContextType = isTeamMember(person.teamRole)
            ? 'team_member'
            : 'contact';
          addContext({ type, id: detected.id, name });
        }
      } catch {
        // Silently fail - auto-context is a convenience feature
      }
    };

    fetchAndAddContext();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
